/**
 * Restrict post-auth redirects to same-site relative paths only (blocks open redirects).
 * Also avoids sending customers to /admin* (Access Denied after signup / non-admin login).
 */

export function isAdminReturnPath(path) {
  if (!path || typeof path !== 'string') return false;
  const p = path.trim();
  return p === '/admin' || p.startsWith('/admin/');
}

export function safeReturnPath(raw, fallback = '/') {
  if (raw == null || typeof raw !== 'string') return fallback;
  const t = raw.trim();
  if (!t.startsWith('/') || t.startsWith('//')) return fallback;
  return t;
}

/**
 * @param {string} raw
 * @param {string} [fallback='/']
 * @param {{ forbidAdmin?: boolean }} [options]
 */
export function safeReturnPathWithOptions(raw, fallback = '/', options = {}) {
  const path = safeReturnPath(raw, fallback);
  if (options.forbidAdmin && isAdminReturnPath(path)) return fallback;
  return path;
}

export const ORDER_HISTORY_PATH = '/profile?tab=orders';

export function pathFromLocationState(from) {
  if (!from?.pathname) return null;
  return `${from.pathname}${from.search || ''}${from.hash || ''}`;
}

/**
 * Resolve post-login destination from ?returnUrl or ProtectedRoute state.from
 * @param {URLSearchParams} searchParams
 * @param {object} locationState
 * @param {string} [fallback='/']
 * @param {{ forbidAdmin?: boolean }} [options]
 */
export function resolveReturnPath(searchParams, locationState, fallback = '/', options = {}) {
  const fromQuery = searchParams.get('returnUrl');
  if (fromQuery) return safeReturnPathWithOptions(fromQuery, fallback, options);
  return safeReturnPathWithOptions(pathFromLocationState(locationState?.from), fallback, options);
}

/**
 * Pick a post-auth destination: admins may go to /admin*; everyone else cannot.
 * @param {string} returnUrl
 * @param {{ roles?: Array<string|{name?: string}> }|null} user
 * @param {string} [fallback='/']
 */
export function destinationAfterAuth(returnUrl, user, fallback = '/') {
  const path = safeReturnPath(returnUrl, fallback);
  if (!isAdminReturnPath(path)) return path;

  const roles = user?.roles || [];
  const isAdmin = roles.some((r) => {
    const name = typeof r === 'string' ? r : r?.name;
    return name === 'admin' || name === 'super_admin';
  });
  return isAdmin ? path : fallback;
}
